import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Paper,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Fab,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
  Divider,
  Grid,
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import { Add, Edit, Delete, ExpandMore, ExpandLess } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { DataGrid } from '@mui/x-data-grid';
import {
  PumpsTab,
  BlowersTab,
  BeltsScreensTab,
  AgitatorsMixersTab,
  DosingPumpsTab,
  SwitchesTab,
  ValvesTab
} from '../components/equipment-tabs';
import {
  addInstrumentToSite,
  getSiteWithInstruments,
  updateInstrumentInSite,
  deleteInstrumentFromSite,
  getTelemetryData,
  logActivity
} from '../api/apiService';
import { TELEMETRY_LIVE_POLL_MS } from '../constants/telemetry';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: '12px',
  backgroundColor: theme.palette.background.paper,
  border: theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)',
  boxShadow: theme.palette.mode === 'dark' ? '0 6px 18px rgba(0,0,0,0.35)' : '0 4px 10px rgba(0,0,0,0.05)',
  '&:hover': {
    boxShadow: theme.palette.mode === 'dark' ? '0 8px 20px rgba(0,0,0,0.45)' : '0 6px 15px rgba(0,0,0,0.1)',
  }
}));

const TableTab = ({ projectId, siteData, tagNames, loadingTags, onInstrumentAdded }) => {
  const userRole = useSelector((state) => state.auth.user?.role);
  const currentUser = useSelector((state) => state.auth.user);
  const isAdminOrManager = userRole === 'Admin' || userRole === 'Manager';
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [expandedIds, setExpandedIds] = useState(() => new Set());

  const formatTimestamp = (val) => {
    if (!val) return '';
    let d = null;
    if (val instanceof Date) d = val;
    else if (typeof val === 'number') {
      const ms = val < 1e12 ? val * 1000 : val;
      d = new Date(ms);
    } else if (typeof val === 'string') {
      const tryDate = new Date(val);
      if (!isNaN(tryDate.getTime())) {
        d = tryDate;
      } else {
        // If it's already in dd/MM/yyyy HH:mm:ss or unparseable, return as-is
        return val;
      }
    }
    if (!d || isNaN(d.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    const day = pad(d.getDate());
    const month = pad(d.getMonth() + 1);
    const year = d.getFullYear();
    const hours = pad(d.getHours());
    const minutes = pad(d.getMinutes());
    const seconds = pad(d.getSeconds());
    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
  };

  // State for sub-tab navigation
  const [selectedSubTab, setSelectedSubTab] = useState(0);

  // State for table functionality
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedInstrument, setSelectedInstrument] = useState(null);
  const [formData, setFormData] = useState({
    selectInstrument: '',
    instrumentDescription: '',
    instrumentType: '',
    location: '',
    tagNo: '',
    unit: '',
    range: '',
    alarmSetPoint: '',
  });
  const [telemetryData, setTelemetryData] = useState({});
  const [isLoadingTelemetry, setIsLoadingTelemetry] = useState(false);
  const isInitialTelemetryFetch = useRef(true);

  useEffect(() => {
    isInitialTelemetryFetch.current = true;
    setTelemetryData({});
  }, [projectId]);

  const formatTagId = useCallback((tagNo) => {
    const serverName = siteData?.serverName || 'PLC1';
    const cleanServerName = serverName.replace(`${projectId}_`, '');
    return `${projectId}_${cleanServerName}:${tagNo}`;
  }, [projectId, siteData?.serverName]);

  const mergeTelemetryResults = useCallback((data) => {
    const updates = {};
    data.forEach((item) => {
      updates[item.tagId] = {
        value: item.value,
        timestamp: item.timestamp,
        localTime: item.localTime,
        isoUtc: item.isoUtc,
        deviceLocalTime: item.deviceLocalTime,
      };
    });
    setTelemetryData((prev) => ({ ...prev, ...updates }));
  }, []);

  const fetchTelemetryForTagIds = useCallback(async (tagIds, { showLoading = false } = {}) => {
    if (!projectId || !tagIds.length) return;

    try {
      if (showLoading) setIsLoadingTelemetry(true);
      const data = await getTelemetryData(projectId, tagIds);
      mergeTelemetryResults(data);
    } catch (error) {
      console.error('[TableTab] Error fetching telemetry data:', error);
    } finally {
      if (showLoading) setIsLoadingTelemetry(false);
    }
  }, [projectId, mergeTelemetryResults]);

  // Fetch instruments data when component mounts or projectId changes
  useEffect(() => {
    const fetchData = async () => {
      if (!projectId) return;

      try {
        setLoading(true);
        const fullSiteData = await getSiteWithInstruments(projectId);
        setRows(fullSiteData.instruments || []);
      } catch (error) {
        console.error('Error fetching site data:', error);
        setRows([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [projectId]);

  // Fetch telemetry data
  useEffect(() => {
    const fetchTelemetryData = async () => {
      if (!projectId || !rows.length) return;

      const tagIds = rows.map((row) => formatTagId(row.tagNo));
      await fetchTelemetryForTagIds(tagIds, { showLoading: isInitialTelemetryFetch.current });
      isInitialTelemetryFetch.current = false;
    };

    fetchTelemetryData();

    const intervalId = setInterval(fetchTelemetryData, TELEMETRY_LIVE_POLL_MS);

    return () => clearInterval(intervalId);
  }, [projectId, rows, formatTagId, fetchTelemetryForTagIds]);

  // Handle Form Data Change
  const handleFormChange = (event) => {
    const { name, value } = event.target;
    
    // If the change is in selectInstrument, also update tagNo
    if (name === 'selectInstrument') {
      setFormData((prevData) => ({
        ...prevData,
        [name]: value,
        tagNo: value // Set tagNo to the same value as selectInstrument
      }));
    } else {
      setFormData((prevData) => ({
        ...prevData,
        [name]: value,
      }));
    }
  };

  // Handle form submission
  const handleSubmit = async () => {
    const newInstrument = {
      id: Date.now(),
      selectInstrument: formData.selectInstrument,
      instrumentDescription: formData.instrumentDescription,
      instrumentType: formData.instrumentType,
      location: formData.location,
      tagNo: formData.tagNo,
      unit: formData.unit,
      range: formData.range,
      alarmSetPoint: formData.alarmSetPoint,
    };

    const emptyForm = {
      selectInstrument: '',
      instrumentDescription: '',
      instrumentType: '',
      location: '',
      tagNo: '',
      unit: '',
      range: '',
      alarmSetPoint: '',
    };

    setRows((prev) => [...prev, newInstrument]);
    setOpenDialog(false);
    setFormData(emptyForm);

    try {
      await addInstrumentToSite(projectId, newInstrument);

      await fetchTelemetryForTagIds([formatTagId(newInstrument.tagNo)]);
      onInstrumentAdded?.();

      try {
        await logActivity(currentUser.email, `Added instrument: ${newInstrument.selectInstrument}`);
      } catch (err) {
        console.error('Error logging activity to DynamoDB:', err);
      }

      const fullSiteData = await getSiteWithInstruments(projectId);
      setRows(fullSiteData.instruments || []);
    } catch (error) {
      console.error('Error adding instrument to DynamoDB:', error);
      setRows((prev) => prev.filter((row) => row.id !== newInstrument.id));
    }
  };

  // Handle Edit
  const handleEdit = (row) => {
    setSelectedInstrument(row);
    setFormData({
      selectInstrument: row.selectInstrument || '',
      instrumentDescription: row.instrumentDescription || '',
      instrumentType: row.instrumentType || '',
      location: row.location || '',
      tagNo: row.tagNo || '',
      unit: row.unit || '',
      range: row.range || '',
      alarmSetPoint: row.alarmSetPoint || '',
    });
    setOpenEditDialog(true);
  };

  // Handle Delete
  const handleDelete = (row) => {
    setSelectedInstrument(row);
    setOpenDeleteDialog(true);
  };

  // Handle Edit Submit
  const handleEditSubmit = async () => {
    try {
      const updatedInstrument = {
        ...selectedInstrument,
        ...formData,
      };
      
      // Ensure we're not creating a new ID
      if (!updatedInstrument.id) {
        updatedInstrument.id = selectedInstrument.id;
      }

      if (userRole === 'Admin' || userRole === 'Manager') {
        await updateInstrumentInSite(projectId, updatedInstrument);
        // Log edit instrument activity
        try {
          await logActivity(currentUser.email, `Edited instrument: ${updatedInstrument.tagNo}`);
        } catch (err) {
          console.error('Error logging edit instrument activity to DynamoDB:', err);
        }
      }
      // Update local state for all roles
      const fullSiteData = await getSiteWithInstruments(projectId);
      setRows(fullSiteData.instruments || []);
      
      // Close dialog and reset state
      setOpenEditDialog(false);
      setSelectedInstrument(null);
      setFormData({
        selectInstrument: '',
        instrumentDescription: '',
        instrumentType: '',
        location: '',
        tagNo: '',
        unit: '',
        range: '',
        alarmSetPoint: '',
      });
    } catch (error) {
      console.error('Error updating instrument:', error);
      // You might want to show an error message to the user here
    }
  };

  // Handle Delete Confirm
  const handleDeleteConfirm = async () => {
    try {
      await deleteInstrumentFromSite(projectId, selectedInstrument.id);
      // Log delete instrument activity
      try {
        await logActivity(currentUser.email, `Deleted instrument: ${selectedInstrument.tagNo}`);
      } catch (err) {
        console.error('Error logging delete instrument activity to DynamoDB:', err);
      }
      console.log('Instrument deleted successfully!');
      const fullSiteData = await getSiteWithInstruments(projectId);
      setRows(fullSiteData.instruments || []);
      setOpenDeleteDialog(false);
      setSelectedInstrument(null);
    } catch (error) {
      console.error('Error deleting instrument:', error);
    }
  };

  // Handle sub-tab change
  const handleSubTabChange = (event, newValue) => {
    setSelectedSubTab(newValue);
  };

  // Define columns for the DataGrid
  const columns = [
    { field: 'tagNo', headerName: 'TAG NO', flex: 0.7 },
    { field: 'instrumentDescription', headerName: 'INSTRUMENT DESCRIPTION', flex: 1.5 },
    { field: 'instrumentType', headerName: 'Instrument Type', flex: 1.0 },
    { field: 'location', headerName: 'Location', flex: 1.0 },
    { 
      field: 'reading', 
      headerName: 'Reading', 
      flex: 1.0,
      renderCell: (params) => {
        const serverName = siteData?.serverName || 'PLC1';
        const cleanServerName = serverName.replace(`${projectId}_`, '');
        const tagId = `${projectId}_${cleanServerName}:${params.row.tagNo}`;
        const telemetryInfo = telemetryData[tagId];
        
        return (
          <Box sx={{ 
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            padding: '0 16px'
          }}>
            {isLoadingTelemetry ? (
              <CircularProgress size={20} sx={{ color: (theme) => theme.palette.primary.main }} />
            ) : telemetryInfo?.value === undefined ? (
              <Typography 
                variant="body2" 
                sx={{ 
                  color: (theme) => theme.palette.text.secondary,
                  fontStyle: 'italic'
                }}
              >
                No data available
              </Typography>
            ) : (
              <Typography 
                variant="body2" 
                sx={{ 
                  color: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main,
                  fontWeight: 500
                }}
              >
                {telemetryInfo.value}
              </Typography>
            )}
          </Box>
        );
      }
    },
    { field: 'unit', headerName: 'UNIT', flex: 0.7 },
    { 
      field: 'timestamp', 
      headerName: 'Last Updated', 
      flex: 1.0,
      renderCell: (params) => {
        const serverName = siteData?.serverName || 'PLC1';
        const cleanServerName = serverName.replace(`${projectId}_`, '');
        const tagId = `${projectId}_${cleanServerName}:${params.row.tagNo}`;
        const telemetryInfo = telemetryData[tagId];
        
        return (
          <Box sx={{ 
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            padding: '0 16px'
          }}>
            {isLoadingTelemetry ? (
              <CircularProgress size={20} sx={{ color: (theme) => theme.palette.primary.main }} />
            ) : telemetryInfo?.localTime ? (
              <Typography 
                variant="body2" 
                sx={{ 
                  color: (theme) => theme.palette.text.secondary
                }}
              >
                {telemetryInfo.localTime}
              </Typography>
            ) : telemetryInfo?.deviceLocalTime ? (
              <Typography 
                variant="body2" 
                sx={{ 
                  color: (theme) => theme.palette.text.secondary
                }}
              >
                {telemetryInfo.deviceLocalTime}
              </Typography>
            ) : (
              <Typography 
                variant="body2" 
                sx={{ 
                  color: (theme) => theme.palette.text.secondary,
                  fontStyle: 'italic'
                }}
              >
                No timestamp available
              </Typography>
            )}
          </Box>
        );
      }
    },
    { field: 'range', headerName: 'RANGE', flex: 0.7 },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.8,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton
            size="small"
            onClick={() => handleEdit(params.row)}
            sx={{
              color: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main,
              '&:hover': {
                backgroundColor: (theme) => alpha(theme.palette.brand?.royalBlue || theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.16 : 0.10),
              },
            }}
          >
            <Edit fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleDelete(params.row)}
            sx={{
              color: (theme) => theme.palette.error.main,
              '&:hover': {
                backgroundColor: (theme) => alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.16 : 0.10),
              },
            }}
          >
            <Delete fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
        <CircularProgress sx={{ color: (theme) => theme.palette.primary.main }} />
      </Box>
    );
  }

  return (
    <Box sx={{ paddingTop: '0px' }}>
      {/* Sub-tab Navigation */}
      <Tabs
        value={selectedSubTab}
        onChange={handleSubTabChange}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{
          backgroundColor: (theme) => theme.palette.background.paper,
          padding: '5px',
          borderRadius: '12px',
          border: (theme) => theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)',
          boxShadow: (theme) => theme.palette.mode === 'dark' ? '0 6px 12px rgba(0,0,0,0.35)' : '0 2px 4px rgba(0, 0, 0, 0.05)',
          mb: 1,
          mt: 0,
          '& .MuiTabs-flexContainer': {
            justifyContent: 'flex-start',
          },
          '& .MuiTabs-scrollButtons': {
            '&.Mui-disabled': {
              opacity: 0.3,
            },
            color: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main,
            '&:hover': {
              backgroundColor: (theme) => alpha(theme.palette.brand?.royalBlue || theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.12 : 0.08),
              color: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main,
            },
          },
          '& .MuiTabs-indicator': {
            height: '3px',
            backgroundColor: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main,
            borderRadius: '12px',
          },
          '& .MuiTab-root': {
            textTransform: 'none',
            fontSize: {
              xs: '0.75rem',
              sm: '0.8rem',
              md: '0.875rem'
            },
            fontWeight: '500',
            color: (theme) => theme.palette.text.secondary,
            padding: {
              xs: '6px 12px',
              sm: '8px 16px',
              md: '10px 20px'
            },
            minWidth: 'auto',
            flexShrink: 0,
            transition: 'all 0.3s ease',
            '&:hover': {
              backgroundColor: (theme) => alpha(theme.palette.brand?.royalBlue || theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.12 : 0.08),
              color: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main,
            },
            '&.Mui-selected': {
              color: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main,
              fontWeight: '600',
            },
          },
        }}
      >
        <Tab label="Instruments" />
        <Tab label="Pumps" />
        <Tab label="Blowers" />
        <Tab label="Belts & Screens" />
        <Tab label="Agitators & Mixers" />
        <Tab label="Dosing Pumps" />
        <Tab label="Switches" />
        <Tab label="Valves" />
      </Tabs>

      {/* Sub-tab Content */}
      {selectedSubTab === 0 && (
        <Box sx={{ height: { xs: '300px', sm: '350px', md: '400px' } }}>
      <StyledPaper
        elevation={0}
        sx={{
          marginTop: '0px',
          width: '100%',
          padding: {
            xs: '12px',
            sm: '14px',
            md: '16px'
          },
          borderRadius: '12px',
          backgroundColor: (theme) => theme.palette.background.paper,
          border: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        {isMobile ? (
          <Stack spacing={1} sx={{ width: '100%' }}>
            {rows.map((row) => {
              const serverName = siteData?.serverName || 'PLC1';
              const cleanServerName = serverName.replace(`${projectId}_`, '');
              const tagId = `${projectId}_${cleanServerName}:${row.tagNo}`;
              const telemetryInfo = telemetryData[tagId];
              const isExpanded = expandedIds.has(row.id);
              return (
                <Paper key={row.id} sx={{ p: 2, borderRadius: '12px', border: (theme) => theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)', backgroundColor: (theme) => theme.palette.background.paper }}>
                  {/* Collapsed header */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Box sx={{ pr: 1 }}>
                      <Typography sx={{ fontWeight: 700, color: (theme) => theme.palette.text.primary }}>{row.tagNo}</Typography>
                      <Typography sx={{ fontSize: '12px', color: (theme) => theme.palette.text.secondary }}>{row.instrumentDescription}</Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography sx={{ color: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main, fontWeight: 600 }}>
                        {isLoadingTelemetry ? '—' : (telemetryInfo?.value ?? '—')} {row.unit || ''}
                      </Typography>
                      <Typography sx={{ fontSize: '11px', color: (theme) => theme.palette.text.secondary }}>
                        {formatTimestamp(telemetryInfo?.localTime || telemetryInfo?.deviceLocalTime)}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                    <Box>
                      <Typography sx={{ fontSize: '12px', color: (theme) => theme.palette.text.secondary }}>{row.location || '-'}</Typography>
                    </Box>
                    <Button 
                      size="small" 
                      variant="contained"
                      endIcon={isExpanded ? <ExpandLess /> : <ExpandMore />} 
                      onClick={() => {
                        const next = new Set(expandedIds);
                        if (next.has(row.id)) next.delete(row.id); else next.add(row.id);
                        setExpandedIds(next);
                      }} 
                      sx={{ 
                        textTransform: 'none',
                        backgroundColor: (theme) => alpha(theme.palette.brand?.royalBlue || theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.22 : 0.14),
                        color: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main,
                        boxShadow: 'none',
                        '&:hover': {
                          backgroundColor: (theme) => alpha(theme.palette.brand?.royalBlue || theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.30 : 0.22),
                          boxShadow: 'none',
                        },
                      }}
                    >
                      {isExpanded ? 'Hide' : 'Details'}
                    </Button>
                  </Box>

                  {isExpanded && (
                    <>
                      <Divider sx={{ my: 1 }} />
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                            <Typography sx={{ fontSize: '12px', color: (theme) => theme.palette.text.secondary }}>Type</Typography>
                            <Typography sx={{ fontSize: '13px', color: (theme) => theme.palette.text.primary, fontWeight: 500 }}>
                              {row.instrumentType || '-'}
                            </Typography>
                            <Typography sx={{ fontSize: '12px', color: (theme) => theme.palette.text.secondary, mt: 1 }}>Range</Typography>
                            <Typography sx={{ fontSize: '13px', color: (theme) => theme.palette.text.primary, fontWeight: 500 }}>
                              {row.range || '-'}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, textAlign: 'right' }}>
                            <Typography sx={{ fontSize: '12px', color: (theme) => theme.palette.text.secondary }}>Unit</Typography>
                            <Typography sx={{ fontSize: '13px', color: (theme) => theme.palette.text.primary, fontWeight: 500 }}>
                              {row.unit || '-'}
                            </Typography>
                            <Typography sx={{ fontSize: '12px', color: (theme) => theme.palette.text.secondary, mt: 1 }}>Alarm Set Point</Typography>
                            <Typography sx={{ fontSize: '13px', color: (theme) => theme.palette.text.primary, fontWeight: 500 }}>
                              {row.alarmSetPoint || '-'}
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                      {isAdminOrManager && (
                        <Stack direction="row" spacing={1} sx={{ mt: 1, justifyContent: 'flex-end' }}>
                          <Button 
                            size="small" 
                            variant="contained" 
                            onClick={() => handleEdit(row)}
                            sx={{
                              backgroundColor: (theme) => alpha(theme.palette.brand?.royalBlue || theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.22 : 0.14),
                              color: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main,
                              boxShadow: 'none',
                              '&:hover': {
                                backgroundColor: (theme) => alpha(theme.palette.brand?.royalBlue || theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.30 : 0.22),
                                boxShadow: 'none',
                              },
                            }}
                          >
                            Edit
                          </Button>
                          <Button 
                            size="small" 
                            variant="contained"
                            onClick={() => handleDelete(row)}
                            sx={{
                              backgroundColor: (theme) => alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.22 : 0.14),
                              color: (theme) => theme.palette.error.main,
                              boxShadow: 'none',
                              '&:hover': {
                                backgroundColor: (theme) => alpha(theme.palette.error.main, theme.palette.mode === 'dark' ? 0.30 : 0.22),
                                boxShadow: 'none',
                              },
                            }}
                          >
                            Delete
                          </Button>
                        </Stack>
                      )}
                    </>
                  )}
                </Paper>
              );
            })}
          </Stack>
        ) : (
          <Box
            sx={{
              height: '100%',
              width: '100%',
              overflowX: 'auto',
              borderRadius: '12px',
              '& .MuiDataGrid-root': {
                border: 'none',
                backgroundColor: (theme) => theme.palette.background.paper,
                borderRadius: '12px',
              },
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: (theme) => (theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.5) : theme.palette.grey[50]),
                color: (theme) => theme.palette.text.secondary,
                fontWeight: '600',
                fontSize: {
                  xs: '12px',
                  sm: '13px',
                  md: '14px'
                },
                borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
              },
              '& .MuiDataGrid-cell': {
                color: (theme) => theme.palette.text.primary,
                fontSize: {
                  xs: '12px',
                  sm: '13px',
                  md: '14px'
                },
                padding: '8px 16px',
                borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                '&:focus': {
                  outline: 'none'
                }
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: (theme) => alpha(theme.palette.brand?.royalBlue || theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.15 : 0.08),
              },
              '& .MuiCheckbox-root': {
                color: (theme) => theme.palette.grey[400],
                '&.Mui-checked': {
                  color: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main,
                },
              },
              '& .MuiDataGrid-footerContainer': {
                backgroundColor: (theme) => (theme.palette.mode === 'dark' ? alpha(theme.palette.background.paper, 0.5) : theme.palette.grey[50]),
                borderTop: (theme) => `1px solid ${theme.palette.divider}`,
              },
              '& .MuiTablePagination-root': {
                color: (theme) => theme.palette.text.secondary,
                '& .MuiIconButton-root': {
                  color: (theme) => theme.palette.text.secondary,
                  '&:hover': {
                    backgroundColor: (theme) => alpha(theme.palette.brand?.royalBlue || theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.12 : 0.08),
                  },
                },
              },
            }}
          >
            <DataGrid
              rows={rows}
              columns={
                (userRole === 'Admin' || userRole === 'Manager')
                  ? columns
                  : columns.filter((col) => col.field !== 'actions')
              }
              pageSize={5}
              rowsPerPageOptions={[5]}
              checkboxSelection
              getRowId={(row) => row.id}
            />
          </Box>
        )}
      </StyledPaper>

      {(userRole === 'Admin' || userRole === 'Manager') && (
        <Fab
          color="primary"
          aria-label="add"
          sx={{
            position: 'fixed',
            bottom: {
              xs: 16,
              sm: 24,
              md: 32
            },
            right: {
              xs: 16,
              sm: 24,
              md: 32
            },
            backgroundColor: (theme) => theme.palette.brand?.royalBlue || theme.palette.primary.main,
            color: (theme) => theme.palette.getContrastText(theme.palette.brand?.royalBlue || theme.palette.primary.main),
            '&:hover': {
              backgroundColor: (theme) => theme.palette.mode === 'dark' 
                ? alpha(theme.palette.brand?.royalBlue || theme.palette.primary.main, 0.85)
                : theme.palette.brand?.royalBlue || theme.palette.primary.main,
              filter: (theme) => theme.palette.mode === 'dark' ? 'none' : 'brightness(0.95)',
            },
          }}
          onClick={() => setOpenDialog(true)}
        >
          <Add />
        </Fab>
      )}

      {/* Add Instrument Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={() => setOpenDialog(false)}
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: { 
            borderRadius: "12px", 
            padding: {
              xs: "12px",
              sm: "16px",
              md: "20px"
            },
            boxShadow: "0px 8px 24px rgba(0,0,0,0.15)"
          }
        }}
      >
        <DialogTitle sx={{ 
          fontWeight: "600", 
          color: "#1A1F36",
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          fontSize: {
            xs: '1.1rem',
            sm: '1.25rem',
            md: '1.5rem'
          }
        }}>
          Add New Instrument
        </DialogTitle>

        <DialogContent>
          <Stack spacing={2} sx={{ marginTop: "16px" }}>
            <FormControl fullWidth>
              <InputLabel id="select-instrument-label">Select Instrument</InputLabel>
              <Select
                labelId="select-instrument-label"
                label="Select Instrument"
                name="selectInstrument"
                value={formData.selectInstrument}
                onChange={handleFormChange}
                variant="outlined"
                disabled={loadingTags}
              >
                <MenuItem value="">Select an instrument</MenuItem>
                {tagNames.map((tagName) => (
                  <MenuItem key={tagName} value={tagName}>
                    {tagName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Instrument Description"
              name="instrumentDescription"
              value={formData.instrumentDescription}
              onChange={handleFormChange}
              variant="outlined"
            />
            <TextField
              fullWidth
              label="Instrument Type"
              name="instrumentType"
              value={formData.instrumentType}
              onChange={handleFormChange}
              variant="outlined"
            />
            <TextField
              fullWidth
              label="Location"
              name="location"
              value={formData.location}
              onChange={handleFormChange}
              variant="outlined"
            />
            <TextField
              fullWidth
              label="Tag No"
              name="tagNo"
              value={formData.tagNo}
              onChange={handleFormChange}
              variant="outlined"
            />
            <TextField
              fullWidth
              label="Unit"
              name="unit"
              value={formData.unit}
              onChange={handleFormChange}
              variant="outlined"
            />
            <TextField
              fullWidth
              label="Range"
              name="range"
              value={formData.range}
              onChange={handleFormChange}
              variant="outlined"
            />
            <TextField
              fullWidth
              label="Alarm Set Point"
              name="alarmSetPoint"
              value={formData.alarmSetPoint}
              onChange={handleFormChange}
              variant="outlined"
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ padding: "16px" }}>
          <Button 
            onClick={() => setOpenDialog(false)} 
            color="secondary" 
            sx={{ 
              textTransform: "none",
              color: '#666',
              '&:hover': {
                backgroundColor: '#F5F5F5',
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            color="primary" 
            sx={{ 
              textTransform: "none", 
              boxShadow: "0px 4px 10px rgba(0,0,0,0.1)",
              backgroundColor: '#1976D2',
              '&:hover': {
                backgroundColor: '#1565C0',
              }
            }}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Instrument Dialog */}
      <Dialog 
        open={openEditDialog} 
        onClose={() => setOpenEditDialog(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px', padding: '16px' } }}
      >
        <DialogTitle sx={{ fontWeight: '600', color: '#1A1F36' }}>Edit Instrument</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ marginTop: '16px' }}>
            <TextField
              fullWidth
              label="Instrument Description"
              name="instrumentDescription"
              value={formData.instrumentDescription}
              onChange={handleFormChange}
              variant="outlined"
            />
            <TextField
              fullWidth
              label="Instrument Type"
              name="instrumentType"
              value={formData.instrumentType}
              onChange={handleFormChange}
              variant="outlined"
            />
            <TextField
              fullWidth
              label="Location"
              name="location"
              value={formData.location}
              onChange={handleFormChange}
              variant="outlined"
            />
            <TextField
              fullWidth
              label="Tag No"
              name="tagNo"
              value={formData.tagNo}
              onChange={handleFormChange}
              variant="outlined"
            />
            <TextField
              fullWidth
              label="Unit"
              name="unit"
              value={formData.unit}
              onChange={handleFormChange}
              variant="outlined"
            />
            <TextField
              fullWidth
              label="Range"
              name="range"
              value={formData.range}
              onChange={handleFormChange}
              variant="outlined"
            />
            <TextField
              fullWidth
              label="Alarm Set Point"
              name="alarmSetPoint"
              value={formData.alarmSetPoint}
              onChange={handleFormChange}
              variant="outlined"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleEditSubmit} 
            variant="contained" 
            color="primary"
          >
            Submit
          </Button>
          <Button 
            onClick={() => setOpenEditDialog(false)} 
            color="secondary"
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Instrument Dialog */}
      <Dialog 
        open={openDeleteDialog} 
        onClose={() => setOpenDeleteDialog(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px', padding: '16px' } }}
      >
        <DialogTitle sx={{ fontWeight: '600', color: '#1A1F36' }}>Delete Instrument</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this instrument?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleDeleteConfirm} 
            variant="contained" 
            color="primary"
          >
            Delete
          </Button>
          <Button 
            onClick={() => setOpenDeleteDialog(false)} 
            color="secondary"
          >
            Cancel
          </Button>
                  </DialogActions>
        </Dialog>
      </Box>
      )}

            {/* Pumps Tab */}
      {selectedSubTab === 1 && <PumpsTab projectId={projectId} siteData={siteData} />}

            {/* Blowers Tab */}
      {selectedSubTab === 2 && <BlowersTab projectId={projectId} siteData={siteData} />}

      {/* Belts & Screens Tab */}
      {selectedSubTab === 3 && <BeltsScreensTab projectId={projectId} siteData={siteData} />}

      {/* Agitators & Mixers Tab */}
      {selectedSubTab === 4 && <AgitatorsMixersTab projectId={projectId} siteData={siteData} />}

      {/* Dosing Pumps Tab */}
      {selectedSubTab === 5 && <DosingPumpsTab projectId={projectId} siteData={siteData} />}

      {/* Switches Tab */}
                  {selectedSubTab === 6 && <SwitchesTab projectId={projectId} siteData={siteData} />}

            {/* Valves Tab */}
                  {selectedSubTab === 7 && <ValvesTab projectId={projectId} siteData={siteData} />}
    </Box>
  );
};

export default TableTab; 